/**
 * User Profile Service
 *
 * Provides user profile data fetching capabilities for displaying user information
 * across the application, particularly for shared trip scenarios where we need
 * to display owner/sharer information.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseInit';

/**
 * Fetch user profile by UID
 *
 * @param {string} userId - User UID to fetch profile for
 * @returns {Promise<Object|null>} User profile with email, displayName, photoURL, or null if not found
 *
 * @example
 * const ownerProfile = await getUserProfile('user123');
 * if (ownerProfile) {
 *   console.log(`Owner: ${ownerProfile.email}`);
 * }
 */
export const getUserProfile = async (userId) => {
  try {
    if (!userId || typeof userId !== 'string') {
      console.error('[userProfileService] Invalid userId:', userId);
      return null;
    }

    const userDoc = await getDoc(doc(db, 'users', userId));

    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        uid: userId,
        email: data.email || null,
        displayName: data.displayName || null,
        photoURL: data.photoURL || null
      };
    }

    console.warn('[userProfileService] User profile not found for userId:', userId);
    return null;
  } catch (error) {
    console.error('[userProfileService] Error fetching user profile:', error);
    return null;
  }
};
